import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import type { Vehicle, Job, Location } from '../types/vrp';
import { useBulkSelection, BulkSelectionItem } from './useBulkSelection';

// Bulk operation types
export interface BulkOperationResult {
  successful: number;
  failed: number;
  errors: Array<{
    row: number;
    field?: string;
    message: string;
  }>;
}

export interface BulkOperationProgress {
  total: number;
  processed: number;
  percentage: number;
  status: 'idle' | 'processing' | 'completed' | 'error';
  message?: string;
}

// CSV/Excel parsing utilities
export const parseFile = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    if (extension === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            reject(new Error(`CSV parsing errors: ${results.errors.map(e => e.message).join(', ')}`));
          } else {
            resolve(results.data);
          }
        },
        error: reject
      });
    } else if (['xlsx', 'xls'].includes(extension || '')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error('Unsupported file format. Please use CSV or Excel files.'));
    }
  });
};

// Data validation functions - Updated for VROOM API compliance
export const validateVehicleData = (data: any): Partial<Vehicle> & { errors: string[] } => {
  const errors: string[] = [];
  const vehicle: Partial<Vehicle> = {};

  // Required: description
  if (!data.description || typeof data.description !== 'string') {
    errors.push('Description is required and must be a string');
  } else {
    vehicle.description = data.description.trim();
  }

  // Validate profile (VROOM supports: car, bike, foot)
  if (data.profile && !['driving', 'cycling', 'foot', 'car', 'bike'].includes(data.profile)) {
    errors.push('Profile must be one of: driving, cycling, foot, car, bike');
  } else {
    // Map frontend profiles to VROOM profiles
    const profileMap: { [key: string]: string } = {
      'driving': 'car',
      'cycling': 'bike', 
      'foot': 'foot',
      'car': 'car',
      'bike': 'bike'
    };
    vehicle.profile = profileMap[data.profile || 'driving'] as any;
  }

  // VROOM Location Validation: Either coordinates OR location_id required
  const hasCoordinates = data.start_lon !== undefined && data.start_lat !== undefined;
  const hasLocationId = data.start_location_id !== undefined;
  
  if (!hasCoordinates && !hasLocationId) {
    errors.push('Either start coordinates (start_lon, start_lat) or start_location_id is required');
  }

  // Validate coordinates if provided
  if (data.start_lon !== undefined) {
    const lon = parseFloat(data.start_lon);
    if (isNaN(lon) || lon < -180 || lon > 180) {
      errors.push('Start longitude must be between -180 and 180');
    } else {
      vehicle.start_lon = lon;
    }
  }

  if (data.start_lat !== undefined) {
    const lat = parseFloat(data.start_lat);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      errors.push('Start latitude must be between -90 and 90');
    } else {
      vehicle.start_lat = lat;
    }
  }

  // Validate end coordinates if provided (optional)
  if (data.end_lon !== undefined) {
    const lon = parseFloat(data.end_lon);
    if (isNaN(lon) || lon < -180 || lon > 180) {
      errors.push('End longitude must be between -180 and 180');
    } else {
      vehicle.end_lon = lon;
    }
  }

  if (data.end_lat !== undefined) {
    const lat = parseFloat(data.end_lat);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      errors.push('End latitude must be between -90 and 90');
    } else {
      vehicle.end_lat = lat;
    }
  }

  // Validate location IDs if provided
  if (data.start_location_id !== undefined) {
    const locationId = parseInt(data.start_location_id);
    if (isNaN(locationId) || locationId <= 0) {
      errors.push('start_location_id must be a positive integer');
    } else {
      vehicle.start_location_id = locationId;
    }
  }

  if (data.end_location_id !== undefined) {
    const locationId = parseInt(data.end_location_id);
    if (isNaN(locationId) || locationId <= 0) {
      errors.push('end_location_id must be a positive integer');
    } else {
      vehicle.end_location_id = locationId;
    }
  }

  // Parse capacity array (VROOM format)
  if (data.capacity) {
    try {
      let capacity;
      if (Array.isArray(data.capacity)) {
        capacity = data.capacity;
      } else if (typeof data.capacity === 'string') {
        // Handle CSV format: "1000,500" or "1000;500"
        capacity = data.capacity.split(/[,;]/).map((c: string) => parseInt(c.trim()));
      } else {
        capacity = JSON.parse(data.capacity);
      }
      
      if (!Array.isArray(capacity) || !capacity.every(c => typeof c === 'number' && c > 0)) {
        errors.push('Capacity must be an array of positive integers');
      } else {
        vehicle.capacity = capacity;
      }
    } catch {
      errors.push('Invalid capacity format - use array or comma-separated values');
    }
  }

  // Parse skills array
  if (data.skills) {
    try {
      let skills;
      if (Array.isArray(data.skills)) {
        skills = data.skills;
      } else if (typeof data.skills === 'string') {
        // Handle CSV format: "1,2,3" or "1;2;3"
        skills = data.skills.split(/[,;]/).map((s: string) => parseInt(s.trim()));
      } else {
        skills = JSON.parse(data.skills);
      }
      
      if (!Array.isArray(skills) || !skills.every(s => typeof s === 'number' && s >= 0)) {
        errors.push('Skills must be an array of non-negative integers');
      } else {
        vehicle.skills = skills;
      }
    } catch {
      errors.push('Invalid skills format - use array or comma-separated values');
    }
  }

  // Validate VROOM-specific constraints
  if (data.speed_factor !== undefined) {
    const speedFactor = parseFloat(data.speed_factor);
    if (isNaN(speedFactor) || speedFactor <= 0 || speedFactor > 5) {
      errors.push('Speed factor must be a number in range (0, 5]');
    } else {
      vehicle.speed_factor = speedFactor;
    }
  }

  if (data.max_tasks !== undefined) {
    const maxTasks = parseInt(data.max_tasks);
    if (isNaN(maxTasks) || maxTasks < 0) {
      errors.push('Max tasks must be a non-negative integer');
    } else {
      vehicle.max_tasks = maxTasks;
    }
  }

  if (data.max_travel_time !== undefined) {
    const maxTravelTime = parseInt(data.max_travel_time);
    if (isNaN(maxTravelTime) || maxTravelTime < 0) {
      errors.push('Max travel time must be a non-negative integer (seconds)');
    } else {
      vehicle.max_travel_time = maxTravelTime;
    }
  }

  if (data.max_distance !== undefined) {
    const maxDistance = parseInt(data.max_distance);
    if (isNaN(maxDistance) || maxDistance < 0) {
      errors.push('Max distance must be a non-negative integer (meters)');
    } else {
      vehicle.max_distance = maxDistance;
    }
  }

  // Parse time windows
  if (data.tw_start !== undefined) {
    const start = parseInt(data.tw_start);
    if (isNaN(start) || start < 0) {
      errors.push('Time window start must be a non-negative number (seconds since midnight)');
    } else {
      vehicle.tw_start = start;
    }
  }

  if (data.tw_end !== undefined) {
    const end = parseInt(data.tw_end);
    if (isNaN(end) || end < 0) {
      errors.push('Time window end must be a non-negative number (seconds since midnight)');
    } else {
      vehicle.tw_end = end;
    }
  }

  // Validate cost fields
  if (data.cost_fixed !== undefined) {
    const costFixed = parseInt(data.cost_fixed);
    if (isNaN(costFixed) || costFixed < 0) {
      errors.push('Fixed cost must be a non-negative integer');
    } else {
      vehicle.cost_fixed = costFixed;
    }
  }

  if (data.cost_per_hour !== undefined) {
    const costPerHour = parseInt(data.cost_per_hour);
    if (isNaN(costPerHour) || costPerHour < 0) {
      errors.push('Cost per hour must be a non-negative integer');
    } else {
      vehicle.cost_per_hour = costPerHour;
    }
  }

  if (data.cost_per_km !== undefined) {
    const costPerKm = parseInt(data.cost_per_km);
    if (isNaN(costPerKm) || costPerKm < 0) {
      errors.push('Cost per km must be a non-negative integer');
    } else {
      vehicle.cost_per_km = costPerKm;
    }
  }

  return { ...vehicle, errors };
};

export const validateJobData = (data: any): Partial<Job> & { errors: string[] } => {
  const errors: string[] = [];
  const job: Partial<Job> = {};

  // Required: description
  if (!data.description || typeof data.description !== 'string') {
    errors.push('Description is required and must be a string');
  } else {
    job.description = data.description.trim();
  }

  // VROOM Location Validation: Either coordinates OR location_id required
  const hasCoordinates = data.location_lon !== undefined && data.location_lat !== undefined;
  const hasLocationId = data.location_id !== undefined;
  
  if (!hasCoordinates && !hasLocationId) {
    errors.push('Either location coordinates (location_lon, location_lat) or location_id is required');
  }

  // Validate coordinates if provided
  if (data.location_lon !== undefined) {
    const lon = parseFloat(data.location_lon);
    if (isNaN(lon) || lon < -180 || lon > 180) {
      errors.push('Location longitude must be between -180 and 180');
    } else {
      job.location_lon = lon;
    }
  }

  if (data.location_lat !== undefined) {
    const lat = parseFloat(data.location_lat);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      errors.push('Location latitude must be between -90 and 90');
    } else {
      job.location_lat = lat;
    }
  }

  // Validate location ID if provided
  if (data.location_id !== undefined) {
    const locationId = parseInt(data.location_id);
    if (isNaN(locationId) || locationId <= 0) {
      errors.push('location_id must be a positive integer');
    } else {
      job.location_id = locationId;
    }
  }

  // Validate time fields
  if (data.setup !== undefined) {
    const setup = parseInt(data.setup);
    if (isNaN(setup) || setup < 0) {
      errors.push('Setup time must be a non-negative number (seconds)');
    } else {
      job.setup = setup;
    }
  }

  if (data.service !== undefined) {
    const service = parseInt(data.service);
    if (isNaN(service) || service < 0) {
      errors.push('Service time must be a non-negative number (seconds)');
    } else {
      job.service = service;
    }
  }

  // Validate priority (Edge Function enforces 1-5 range)
  if (data.priority !== undefined) {
    const priority = parseInt(data.priority);
    if (isNaN(priority) || priority < 1 || priority > 5) {
      errors.push('Priority must be between 1 and 5 (Edge Function constraint)');
    } else {
      job.priority = priority;
    }
  }

  // Parse delivery amounts array
  if (data.delivery) {
    try {
      let delivery;
      if (Array.isArray(data.delivery)) {
        delivery = data.delivery;
      } else if (typeof data.delivery === 'string') {
        // Handle CSV format: "10,5" or "10;5"
        delivery = data.delivery.split(/[,;]/).map((d: string) => parseInt(d.trim()));
      } else {
        delivery = JSON.parse(data.delivery);
      }
      
      if (!Array.isArray(delivery) || !delivery.every(d => typeof d === 'number' && d >= 0)) {
        errors.push('Delivery amounts must be an array of non-negative integers');
      } else {
        job.delivery = delivery;
      }
    } catch {
      errors.push('Invalid delivery format - use array or comma-separated values');
    }
  }

  // Parse pickup amounts array
  if (data.pickup) {
    try {
      let pickup;
      if (Array.isArray(data.pickup)) {
        pickup = data.pickup;
      } else if (typeof data.pickup === 'string') {
        // Handle CSV format: "5,3" or "5;3"
        pickup = data.pickup.split(/[,;]/).map((p: string) => parseInt(p.trim()));
      } else {
        pickup = JSON.parse(data.pickup);
      }
      
      if (!Array.isArray(pickup) || !pickup.every(p => typeof p === 'number' && p >= 0)) {
        errors.push('Pickup amounts must be an array of non-negative integers');
      } else {
        job.pickup = pickup;
      }
    } catch {
      errors.push('Invalid pickup format - use array or comma-separated values');
    }
  }

  // Parse skills array
  if (data.skills) {
    try {
      let skills;
      if (Array.isArray(data.skills)) {
        skills = data.skills;
      } else if (typeof data.skills === 'string') {
        // Handle CSV format: "1,2,3" or "1;2;3"
        skills = data.skills.split(/[,;]/).map((s: string) => parseInt(s.trim()));
      } else {
        skills = JSON.parse(data.skills);
      }
      
      if (!Array.isArray(skills) || !skills.every(s => typeof s === 'number' && s >= 0)) {
        errors.push('Skills must be an array of non-negative integers');
      } else {
        job.skills = skills;
      }
    } catch {
      errors.push('Invalid skills format - use array or comma-separated values');
    }
  }

  // Parse time windows array
  if (data.time_windows) {
    try {
      let timeWindows;
      if (Array.isArray(data.time_windows)) {
        timeWindows = data.time_windows;
      } else if (typeof data.time_windows === 'string') {
        // Handle CSV format: "28800-43200" or "28800-43200,50400-64800"
        timeWindows = data.time_windows.split(',').map((tw: string) => {
          const [start, end] = tw.trim().split('-');
          return { start: parseInt(start), end: parseInt(end) };
        });
      } else {
        timeWindows = JSON.parse(data.time_windows);
      }
      
      if (!Array.isArray(timeWindows) || !timeWindows.every(tw => 
        tw && typeof tw.start === 'number' && typeof tw.end === 'number' && 
        tw.start >= 0 && tw.end >= tw.start
      )) {
        errors.push('Time windows must be an array of objects with start/end times (seconds since midnight)');
      } else {
        job.time_windows = timeWindows;
      }
    } catch {
      errors.push('Invalid time windows format - use array or "start-end,start-end" format');
    }
  }

  return { ...job, errors };
};

export const validateLocationData = (data: any): Partial<Location> & { errors: string[] } => {
  const errors: string[] = [];
  const location: Partial<Location> = {};

  // Required: name
  if (!data.name || typeof data.name !== 'string') {
    errors.push('Name is required and must be a string');
  } else {
    location.name = data.name.trim();
  }

  // VROOM Location Validation: Either coordinates OR address required for geocoding
  const hasCoordinates = data.location_lon !== undefined && data.location_lat !== undefined;
  const hasAddress = data.address && typeof data.address === 'string' && data.address.trim().length > 0;
  
  if (!hasCoordinates && !hasAddress) {
    errors.push('Either location coordinates (location_lon, location_lat) or address is required');
  }

  // Validate coordinates if provided
  if (data.location_lon !== undefined) {
    const lon = parseFloat(data.location_lon);
    if (isNaN(lon) || lon < -180 || lon > 180) {
      errors.push('Longitude must be between -180 and 180');
    } else {
      location.location_lon = lon;
    }
  }

  if (data.location_lat !== undefined) {
    const lat = parseFloat(data.location_lat);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      errors.push('Latitude must be between -90 and 90');
    } else {
      location.location_lat = lat;
    }
  }

  // Validate address
  if (data.address) {
    if (typeof data.address === 'string' && data.address.trim().length > 0) {
      location.address = data.address.trim();
    } else {
      errors.push('Address must be a non-empty string');
    }
  }

  // Validate location type
  if (data.location_type) {
    const validTypes = ['depot', 'customer', 'warehouse', 'service_point', 'pickup_point', 'delivery_point'];
    if (typeof data.location_type === 'string' && validTypes.includes(data.location_type)) {
      location.location_type = data.location_type.trim();
    } else {
      errors.push(`Location type must be one of: ${validTypes.join(', ')}`);
    }
  } else {
    // Default to customer if not specified
    location.location_type = 'customer';
  }

  // Validate cluster ID if provided
  if (data.cluster_id !== undefined) {
    const clusterId = parseInt(data.cluster_id);
    if (isNaN(clusterId) || clusterId <= 0) {
      errors.push('cluster_id must be a positive integer');
    } else {
      location.cluster_id = clusterId;
    }
  }

  // Validate enhanced fields
  if (data.description !== undefined) {
    if (typeof data.description === 'string') {
      location.description = data.description.trim();
    } else {
      errors.push('Description must be a string');
    }
  }

  if (data.operating_hours !== undefined) {
    if (typeof data.operating_hours === 'string') {
      // Basic validation for operating hours format
      const hours = data.operating_hours.trim();
      if (hours === '24/7' || /^\d{1,2}:\d{2}-\d{1,2}:\d{2}$/.test(hours)) {
        location.operating_hours = hours;
      } else {
        errors.push('Operating hours must be in format "HH:MM-HH:MM" or "24/7"');
      }
    } else {
      errors.push('Operating hours must be a string');
    }
  }

  if (data.contact_info !== undefined) {
    if (typeof data.contact_info === 'string') {
      location.contact_info = data.contact_info.trim();
    } else {
      errors.push('Contact info must be a string');
    }
  }

  return { ...location, errors };
};

// Bulk operations hooks
export const useBulkVehicleOperations = () => {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BulkOperationProgress>({
    total: 0,
    processed: 0,
    percentage: 0,
    status: 'idle'
  });

  const bulkCreate = useMutation({
    mutationFn: async ({ projectId, data }: { projectId: number; data: any[] }) => {
      setProgress({ total: data.length, processed: 0, percentage: 0, status: 'processing' });
      
      const results: BulkOperationResult = {
        successful: 0,
        failed: 0,
        errors: []
      };

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        const validated = validateVehicleData(item);
        
        if (validated.errors.length > 0) {
          results.failed++;
          results.errors.push({
            row: i + 1,
            message: validated.errors.join(', ')
          });
        } else {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const response = await fetch('https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/vehicles', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({ ...validated, project_id: projectId }),
            });

            if (response.ok) {
              results.successful++;
            } else {
              results.failed++;
              results.errors.push({
                row: i + 1,
                message: 'Failed to create vehicle'
              });
            }
          } catch (error) {
            results.failed++;
            results.errors.push({
              row: i + 1,
              message: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }

        setProgress(prev => ({
          ...prev,
          processed: i + 1,
          percentage: Math.round(((i + 1) / data.length) * 100)
        }));
      }

      setProgress(prev => ({ ...prev, status: 'completed' }));
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    }
  });

  return {
    bulkCreate,
    progress,
    resetProgress: () => setProgress({ total: 0, processed: 0, percentage: 0, status: 'idle' })
  };
};

export const useBulkJobOperations = () => {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BulkOperationProgress>({
    total: 0,
    processed: 0,
    percentage: 0,
    status: 'idle'
  });

  const bulkCreate = useMutation({
    mutationFn: async ({ projectId, data }: { projectId: number; data: any[] }) => {
      setProgress({ total: data.length, processed: 0, percentage: 0, status: 'processing' });
      
      const results: BulkOperationResult = {
        successful: 0,
        failed: 0,
        errors: []
      };

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        const validated = validateJobData(item);
        
        if (validated.errors.length > 0) {
          results.failed++;
          results.errors.push({
            row: i + 1,
            message: validated.errors.join(', ')
          });
        } else {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const response = await fetch('https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/jobs', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({ ...validated, project_id: projectId }),
            });

            if (response.ok) {
              results.successful++;
            } else {
              results.failed++;
              results.errors.push({
                row: i + 1,
                message: 'Failed to create job'
              });
            }
          } catch (error) {
            results.failed++;
            results.errors.push({
              row: i + 1,
              message: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }

        setProgress(prev => ({
          ...prev,
          processed: i + 1,
          percentage: Math.round(((i + 1) / data.length) * 100)
        }));
      }

      setProgress(prev => ({ ...prev, status: 'completed' }));
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    }
  });

  return {
    bulkCreate,
    progress,
    resetProgress: () => setProgress({ total: 0, processed: 0, percentage: 0, status: 'idle' })
  };
};

export const useBulkLocationOperations = () => {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BulkOperationProgress>({
    total: 0,
    processed: 0,
    percentage: 0,
    status: 'idle'
  });

  const bulkCreate = useMutation({
    mutationFn: async ({ projectId, data }: { projectId: number; data: any[] }) => {
      setProgress({ total: data.length, processed: 0, percentage: 0, status: 'processing' });
      
      const results: BulkOperationResult = {
        successful: 0,
        failed: 0,
        errors: []
      };

      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        const validated = validateLocationData(item);
        
        if (validated.errors.length > 0) {
          results.failed++;
          results.errors.push({
            row: i + 1,
            message: validated.errors.join(', ')
          });
        } else {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const response = await fetch('https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/locations', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({ ...validated, project_id: projectId }),
            });

            if (response.ok) {
              results.successful++;
            } else {
              results.failed++;
              results.errors.push({
                row: i + 1,
                message: 'Failed to create location'
              });
            }
          } catch (error) {
            results.failed++;
            results.errors.push({
              row: i + 1,
              message: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }

        setProgress(prev => ({
          ...prev,
          processed: i + 1,
          percentage: Math.round(((i + 1) / data.length) * 100)
        }));
      }

      setProgress(prev => ({ ...prev, status: 'completed' }));
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    }
  });

  return {
    bulkCreate,
    progress,
    resetProgress: () => setProgress({ total: 0, processed: 0, percentage: 0, status: 'idle' })
  };
};

// Export utilities
export const exportToCSV = (data: any[], filename: string) => {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToExcel = (data: any[], filename: string) => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  XLSX.writeFile(wb, filename);
};

// Enhanced bulk operations with selection support
export const useEnhancedBulkOperations = <T extends BulkSelectionItem>(
  items: T[],
  operationType: 'vehicles' | 'jobs' | 'locations'
) => {
  const queryClient = useQueryClient();
  const bulkSelection = useBulkSelection(items);
  
  const [progress, setProgress] = useState<BulkOperationProgress>({
    total: 0,
    processed: 0,
    percentage: 0,
    status: 'idle'
  });

  // Bulk delete selected items
  const bulkDelete = useMutation({
    mutationFn: async (selectedIds: string[]) => {
      setProgress({ 
        total: selectedIds.length, 
        processed: 0, 
        percentage: 0, 
        status: 'processing',
        message: `Deleting ${selectedIds.length} ${operationType}...`
      });
      
      const results: BulkOperationResult = {
        successful: 0,
        failed: 0,
        errors: []
      };

      const { data: { session } } = await supabase.auth.getSession();
      const baseUrl = `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/${operationType}`;

      for (let i = 0; i < selectedIds.length; i++) {
        const id = selectedIds[i];
        
        try {
          const response = await fetch(`${baseUrl}/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${session?.access_token}`,
            },
          });

          if (response.ok) {
            results.successful++;
          } else {
            results.failed++;
            results.errors.push({
              row: i + 1,
              message: `Failed to delete ${operationType.slice(0, -1)} ${id}`
            });
          }
        } catch (error) {
          results.failed++;
          results.errors.push({
            row: i + 1,
            message: error instanceof Error ? error.message : 'Unknown error'
          });
        }

        setProgress(prev => ({
          ...prev,
          processed: i + 1,
          percentage: Math.round(((i + 1) / selectedIds.length) * 100)
        }));
      }

      setProgress(prev => ({ ...prev, status: 'completed' }));
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [operationType] });
      bulkSelection.clearSelection();
    }
  });

  // Bulk update selected items
  const bulkUpdate = useMutation({
    mutationFn: async ({ selectedIds, updates }: { selectedIds: string[]; updates: Partial<T> }) => {
      setProgress({ 
        total: selectedIds.length, 
        processed: 0, 
        percentage: 0, 
        status: 'processing',
        message: `Updating ${selectedIds.length} ${operationType}...`
      });
      
      const results: BulkOperationResult = {
        successful: 0,
        failed: 0,
        errors: []
      };

      const { data: { session } } = await supabase.auth.getSession();
      const baseUrl = `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/${operationType}`;

      for (let i = 0; i < selectedIds.length; i++) {
        const id = selectedIds[i];
        
        try {
          const response = await fetch(`${baseUrl}/${id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify(updates),
          });

          if (response.ok) {
            results.successful++;
          } else {
            results.failed++;
            results.errors.push({
              row: i + 1,
              message: `Failed to update ${operationType.slice(0, -1)} ${id}`
            });
          }
        } catch (error) {
          results.failed++;
          results.errors.push({
            row: i + 1,
            message: error instanceof Error ? error.message : 'Unknown error'
          });
        }

        setProgress(prev => ({
          ...prev,
          processed: i + 1,
          percentage: Math.round(((i + 1) / selectedIds.length) * 100)
        }));
      }

      setProgress(prev => ({ ...prev, status: 'completed' }));
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [operationType] });
      bulkSelection.clearSelection();
    }
  });

  // Bulk export selected items
  const bulkExport = useCallback((format: 'csv' | 'excel') => {
    const selectedItems = bulkSelection.selectedItems;
    const filename = `${operationType}_export_${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'xlsx'}`;
    
    if (format === 'csv') {
      exportToCSV(selectedItems, filename);
    } else {
      exportToExcel(selectedItems, filename);
    }
  }, [bulkSelection.selectedItems, operationType]);

  // Bulk duplicate selected items
  const bulkDuplicate = useMutation({
    mutationFn: async (selectedIds: string[]) => {
      setProgress({ 
        total: selectedIds.length, 
        processed: 0, 
        percentage: 0, 
        status: 'processing',
        message: `Duplicating ${selectedIds.length} ${operationType}...`
      });
      
      const results: BulkOperationResult = {
        successful: 0,
        failed: 0,
        errors: []
      };

      const { data: { session } } = await supabase.auth.getSession();
      const baseUrl = `https://xzwsmladddtdgsrkiwlg.supabase.co/functions/v1/${operationType}`;

      for (let i = 0; i < selectedIds.length; i++) {
        const id = selectedIds[i];
        const originalItem = items.find(item => item.id === id);
        
        if (!originalItem) {
          results.failed++;
          results.errors.push({
            row: i + 1,
            message: `Original ${operationType.slice(0, -1)} not found`
          });
          continue;
        }

        try {
          // Create a copy with modified name/description
          const { id: _, ...itemCopy } = originalItem as any;
          const duplicatedItem = {
            ...itemCopy,
            description: `${itemCopy.description} (Copy)`,
            name: itemCopy.name ? `${itemCopy.name} (Copy)` : undefined
          };

          const response = await fetch(baseUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify(duplicatedItem),
          });

          if (response.ok) {
            results.successful++;
          } else {
            results.failed++;
            results.errors.push({
              row: i + 1,
              message: `Failed to duplicate ${operationType.slice(0, -1)} ${id}`
            });
          }
        } catch (error) {
          results.failed++;
          results.errors.push({
            row: i + 1,
            message: error instanceof Error ? error.message : 'Unknown error'
          });
        }

        setProgress(prev => ({
          ...prev,
          processed: i + 1,
          percentage: Math.round(((i + 1) / selectedIds.length) * 100)
        }));
      }

      setProgress(prev => ({ ...prev, status: 'completed' }));
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [operationType] });
      bulkSelection.clearSelection();
    }
  });

  return {
    ...bulkSelection,
    progress,
    bulkDelete,
    bulkUpdate,
    bulkExport,
    bulkDuplicate,
    resetProgress: () => setProgress({ total: 0, processed: 0, percentage: 0, status: 'idle' })
  };
};
